import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  DevHubAlert,
  DevHubBox,
  DevHubCircularProgress,
  DevHubLink,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import AppInfoItem from '../common/AppInfoItem';
import { GetADGroupsQuery } from './types/onboardingTypes';

interface ADGroupsDisplayProps {
  applicationId: number;
}

/**
 * Component to display AD groups for a newly created application.
 * Shows loading state while fetching and error state if creation failed.
 *
 * @param props - Component props
 * @param props.applicationId - The application ID to fetch AD groups for
 */
export const ADGroupsDisplay = ({ applicationId }: ADGroupsDisplayProps) => {
  const [adGroups, setAdGroups] = useState<string[]>([]);
  const [adGroupsError, setAdGroupsError] = useState<boolean>(false);
  const [adGroupsLoading, setAdGroupsLoading] = useState<boolean>(false);

  const [fetchADGroups] = useLazyQuery(GetADGroupsQuery, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      const applicationAdGroups: { groupId: string; groupName: string }[] =
        data?.resources?.application?.resources?.adGroupsPaged?.adGroups || [];

      setAdGroupsLoading(false);
      // Extract successfully created group names
      if (applicationAdGroups.length > 0) {
        const groupNames = applicationAdGroups
          .map(group => group.groupName)
          .sort();

        if (groupNames.length < 3) {
          setAdGroupsError(true);
        }

        setAdGroups(groupNames);
      } else {
        setAdGroupsError(true);
      }
    },
    onError() {
      setAdGroupsError(true);
      setAdGroupsLoading(false);
    },
  });

  useEffect(() => {
    if (applicationId > 0) {
      setAdGroupsLoading(true);
      const timeoutId = setTimeout(() => {
        fetchADGroups({
          variables: {
            applicationId: applicationId,
          },
        });
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [applicationId, fetchADGroups]);

  return (
    <>
      <AppInfoItem
        primaryText={`AD Groups${adGroupsLoading ? '' : ` (${adGroups.length || 0})`}:`}
        secondaryText={
          adGroupsLoading ? (
            <DevHubBox display="flex" alignItems="center">
              <DevHubCircularProgress size={20} />
              <DevHubBox ml={1}>
                <DevHubTypography variant="body2" color="textSecondary">
                  Fetching AD groups...
                </DevHubTypography>
              </DevHubBox>
            </DevHubBox>
          ) : (
            <DevHubBox
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
            >
              {adGroups?.map(group => (
                <DevHubTypography key={group}>{group}</DevHubTypography>
              ))}
            </DevHubBox>
          )
        }
      />

      {adGroupsError && (
        <DevHubBox mt={2} width="100%">
          <DevHubAlert severity="error">
            <DevHubTypography variant="body1">
              <strong>Error creating AD group(s).</strong> Some or all AD groups
              may not have been created successfully.
            </DevHubTypography>
            <DevHubTypography variant="body2">
              Please reach out to the{' '}
              <DevHubLink
                href="https://cim.slack.com/archives/C02N9B1E4QH"
                target="_blank"
                rel="noopener noreferrer"
              >
                #devhub
              </DevHubLink>{' '}
              slack or{' '}
              <DevHubLink
                href="https://teams.microsoft.com/l/channel/19%3A9c2c316f92954d2f9ae6080dd6444ff7%40thread.skype/General?groupId=d3388271-730e-40b5-a856-73be785c0c25&tenantId="
                target="_blank"
                rel="noopener noreferrer"
              >
                devhub teams
              </DevHubLink>{' '}
              channel for support.
            </DevHubTypography>
          </DevHubAlert>
        </DevHubBox>
      )}
    </>
  );
};
